import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import MiniDashboard from '../components/MiniDashboard';
import { formatDate } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function Shifts({ user, onLogout }) {
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [showShiftForm, setShowShiftForm] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [activeTab, setActiveTab] = useState('shifts');
  const [dashboardStats, setDashboardStats] = useState(null);
  
  const [shiftFormData, setShiftFormData] = useState({
    name: '',
    start_time: '09:00',
    end_time: '18:00',
    break_duration: 60,
    applicable_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
  });
  
  const [scheduleFormData, setScheduleFormData] = useState({
    employee_ids: [],
    shift_id: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: ''
  });

  useEffect(() => {
    fetchShifts();
    fetchSchedules();
    fetchDashboardStats();
    if (user.role === 'Admin' || user.role === 'HR Manager') {
      fetchEmployees();
    }
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data.filter(emp => emp.employee_status === 'Active'));
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const fetchShifts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/shifts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShifts(response.data);
    } catch (error) {
      console.error('Failed to fetch shifts:', error);
    }
  };

  const fetchSchedules = async () => {
    try {
      const token = localStorage.getItem('token');
      const today = new Date().toISOString().split('T')[0];
      const nextWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const response = await axios.get(`${API}/schedules?start_date=${today}&end_date=${nextWeek}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedules(response.data);
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
    }
  };

  const handleShiftSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (selectedShift) {
        await axios.put(`${API}/shifts/${selectedShift.id}`, shiftFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Shift updated successfully!');
      } else {
        await axios.post(`${API}/shifts`, shiftFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Shift created successfully!');
      }
      setShowShiftForm(false);
      setSelectedShift(null);
      resetShiftForm();
      fetchShifts();
    } catch (error) {
      alert('Failed to save shift: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/schedules/bulk`, scheduleFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Schedules created successfully!');
      setShowScheduleForm(false);
      resetScheduleForm();
      fetchSchedules();
    } catch (error) {
      alert('Failed to create schedules: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteShift = async (shiftId) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/shifts/${shiftId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Shift deleted!');
      fetchShifts();
    } catch (error) {
      alert('Failed to delete: ' + error.message);
    }
  };

  const resetShiftForm = () => {
    setShiftFormData({
      name: '',
      start_time: '09:00',
      end_time: '18:00',
      break_duration: 60,
      applicable_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
    });
  };

  const resetScheduleForm = () => {
    setScheduleFormData({
      employee_ids: [],
      shift_id: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: ''
    });
  };

  const handleDayToggle = (day) => {
    const current = shiftFormData.applicable_days;
    if (current.includes(day)) {
      setShiftFormData({ ...shiftFormData, applicable_days: current.filter(d => d !== day) });
    } else {
      setShiftFormData({ ...shiftFormData, applicable_days: [...current, day] });
    }
  };

  const handleEmployeeToggle = (empId) => {
    const current = scheduleFormData.employee_ids;
    if (current.includes(empId)) {
      setScheduleFormData({ ...scheduleFormData, employee_ids: current.filter(id => id !== empId) });
    } else {
      setScheduleFormData({ ...scheduleFormData, employee_ids: [...current, empId] });
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Shift & Schedule Management</h1>
        </div>

        {/* Shifts Management Mini Dashboard */}
        {dashboardStats && (
          <MiniDashboard
            title="Shifts & Schedule Overview"
            icon="⏰"
            bgColor="#607d8b"
            stats={[
              {
                label: 'Total Shifts',
                value: dashboardStats.total_shifts || 0,
                subtitle: 'Configured shifts',
                color: '#607d8b'
              },
              {
                label: 'Present Today',
                value: dashboardStats.present_today || 0,
                subtitle: 'On duty',
                color: '#4caf50'
              },
              {
                label: 'Total Employees',
                value: dashboardStats.total_employees || 0,
                subtitle: 'Shift assignments',
                color: '#2b3380'
              },
              {
                label: 'Coverage',
                value: '100%',
                subtitle: 'Shift coverage',
                color: '#00acc1'
              }
            ]}
          />
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('shifts')}
            className={`px-6 py-2 rounded-lg font-medium ${activeTab === 'shifts' ? 'text-white' : 'text-gray-700 bg-gray-200'}`}
            style={{backgroundColor: activeTab === 'shifts' ? '#2b3380' : undefined}}
          >
            Shifts
          </button>
          <button
            onClick={() => setActiveTab('schedules')}
            className={`px-6 py-2 rounded-lg font-medium ${activeTab === 'schedules' ? 'text-white' : 'text-gray-700 bg-gray-200'}`}
            style={{backgroundColor: activeTab === 'schedules' ? '#2b3380' : undefined}}
          >
            Schedules
          </button>
        </div>

        {/* Shifts Tab */}
        {activeTab === 'shifts' && (
          <div>
            {(user.role === 'Admin' || user.role === 'HR Manager') && (
              <button
                onClick={() => { setShowShiftForm(true); setSelectedShift(null); resetShiftForm(); }}
                className="mb-4 px-6 py-2 rounded-lg text-white"
                style={{backgroundColor: '#2b3380'}}
              >
                Create New Shift
              </button>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {shifts.map(shift => (
                <div key={shift.id} className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-xl font-bold mb-2" style={{color: '#2b3380'}}>{shift.name}</h3>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>🕐 {shift.start_time} - {shift.end_time}</p>
                    <p>☕ Break: {shift.break_duration} mins</p>
                    <p>📅 Days: {shift.applicable_days?.join(', ')}</p>
                  </div>
                  {(user.role === 'Admin' || user.role === 'HR Manager') && (
                    <div className="mt-4 flex gap-2">
                      <button
                        onClick={() => handleDeleteShift(shift.id)}
                        className="text-red-600 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Schedules Tab */}
        {activeTab === 'schedules' && (
          <div>
            {(user.role === 'Admin' || user.role === 'HR Manager') && (
              <button
                onClick={() => { setShowScheduleForm(true); resetScheduleForm(); }}
                className="mb-4 px-6 py-2 rounded-lg text-white"
                style={{backgroundColor: '#2b3380'}}
              >
                Assign Schedules
              </button>
            )}

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <table className="w-full">
                <thead style={{backgroundColor: '#2b3380', color: 'white'}}>
                  <tr>
                    <th className="px-4 py-3 text-left">Employee</th>
                    <th className="px-4 py-3 text-left">Shift</th>
                    <th className="px-4 py-3 text-left">Time</th>
                    <th className="px-4 py-3 text-left">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.map(schedule => (
                    <tr key={schedule.id} className="border-t">
                      <td className="px-4 py-3">{schedule.employee_name}</td>
                      <td className="px-4 py-3">{schedule.shift_name}</td>
                      <td className="px-4 py-3">{schedule.shift_start_time} - {schedule.shift_end_time}</td>
                      <td className="px-4 py-3">{formatDate(schedule.date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Shift Form Modal */}
        {showShiftForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
              <h2 className="text-2xl font-bold mb-4" style={{color: '#2b3380'}}>Create Shift</h2>
              <form onSubmit={handleShiftSubmit} className="space-y-4">
                <input type="text" value={shiftFormData.name} onChange={(e) => setShiftFormData({...shiftFormData, name: e.target.value})} required placeholder="Shift Name" className="w-full px-3 py-2 border rounded-lg" />
                <div className="grid grid-cols-2 gap-4">
                  <input type="time" value={shiftFormData.start_time} onChange={(e) => setShiftFormData({...shiftFormData, start_time: e.target.value})} required className="px-3 py-2 border rounded-lg" />
                  <input type="time" value={shiftFormData.end_time} onChange={(e) => setShiftFormData({...shiftFormData, end_time: e.target.value})} required className="px-3 py-2 border rounded-lg" />
                </div>
                <input type="number" value={shiftFormData.break_duration} onChange={(e) => setShiftFormData({...shiftFormData, break_duration: parseInt(e.target.value)})} required placeholder="Break (mins)" className="w-full px-3 py-2 border rounded-lg" />
                <div>
                  <label className="block mb-2 font-medium">Applicable Days</label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS.map(day => (
                      <label key={day} className="flex items-center">
                        <input type="checkbox" checked={shiftFormData.applicable_days.includes(day)} onChange={() => handleDayToggle(day)} className="mr-2" />
                        {day}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3 justify-end">
                  <button type="button" onClick={() => setShowShiftForm(false)} className="px-6 py-2 border rounded-lg">Cancel</button>
                  <button type="submit" className="px-6 py-2 rounded-lg text-white" style={{backgroundColor: '#2b3380'}}>Create</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Schedule Form Modal */}
        {showScheduleForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-4" style={{color: '#2b3380'}}>Assign Schedules</h2>
              <form onSubmit={handleScheduleSubmit} className="space-y-4">
                <div>
                  <label className="block mb-2 font-medium">Select Employees</label>
                  <div className="max-h-40 overflow-y-auto border rounded p-2">
                    {employees.map(emp => (
                      <label key={emp.id} className="flex items-center mb-1">
                        <input type="checkbox" checked={scheduleFormData.employee_ids.includes(emp.id)} onChange={() => handleEmployeeToggle(emp.id)} className="mr-2" />
                        {emp.first_name} {emp.last_name}
                      </label>
                    ))}
                  </div>
                </div>
                <select value={scheduleFormData.shift_id} onChange={(e) => setScheduleFormData({...scheduleFormData, shift_id: e.target.value})} required className="w-full px-3 py-2 border rounded-lg">
                  <option value="">Select Shift</option>
                  {shifts.map(shift => <option key={shift.id} value={shift.id}>{shift.name}</option>)}
                </select>
                <div className="grid grid-cols-2 gap-4">
                  <input type="date" value={scheduleFormData.start_date} onChange={(e) => setScheduleFormData({...scheduleFormData, start_date: e.target.value})} required className="px-3 py-2 border rounded-lg" />
                  <input type="date" value={scheduleFormData.end_date} onChange={(e) => setScheduleFormData({...scheduleFormData, end_date: e.target.value})} required className="px-3 py-2 border rounded-lg" />
                </div>
                <div className="flex gap-3 justify-end">
                  <button type="button" onClick={() => setShowScheduleForm(false)} className="px-6 py-2 border rounded-lg">Cancel</button>
                  <button type="submit" className="px-6 py-2 rounded-lg text-white" style={{backgroundColor: '#2b3380'}}>Assign</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Shifts;